<script setup lang="ts">
import useVueformResponseErrors from 'vueform/src/VueformResponseErrors';
import {ref, type Ref} from 'vue';
import type {JText} from 'joomla/joomla.interface';
import type {SelectOption} from '@/interfaces/SelectOption.interface';
import type {FormConfigs} from '@/interfaces/FormConfigs.inteface';

const completed: Ref<boolean> = ref(false)
const jText: JText = Joomla.JText
const config: FormConfigs = Joomla.getOptions('configuration')
const educationLevels: SelectOption[] = config.educationLevels
const employmentDurations: SelectOption[] = config.employmentDurations
const vacancyId: string = config.vacancyId
const showCertificateField: boolean = config.showCertificateField

function success(): void {
  document.querySelector<HTMLElement>('#lvm-vacancy-success')!.style.display = 'block'
  document.querySelector<HTMLElement>('.vacancy-form .intro')!.style.display = 'none'
  completed.value = true
}
</script>
<template>
  <Vueform
      ref="form"
      v-if="!completed"
      form-key="lvm-full-vacancy"
      @success="success"
      @response="useVueformResponseErrors as any"
  >
    <TextElement
        name="name"
        rules="required"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_NAME')"
        :columns="{ default: 12, md: 5 }"
    />
    <TextElement
        name="email"
        rules="required|email:debounce=1000"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_EMAIL')"
        :columns="{ default: 12, md: 7 }"
    />
    <SelectElement
        name="education"
        rules="required"
        :placeholder="jText._('MOD_REACTIVE_FORMS_LVM_VACANCY_EDUCATION_PLACEHOLDER')"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_EDUCATION')"
        :native="false"
        :floating="false"
        :items="educationLevels"
        :columns="{ default: 12, md: 6 }"
    />
    <TextElement
        name="qualification"
        rules="required"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_QUALIFICATION')"
        :columns="{ default: 12, md: 6 }"
    />
    <TextElement
        v-if="showCertificateField"
        name="certificate"
        rules="required"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_CERTIFICATE')"
        :columns="{ default: 12, md: 6 }"
    />
    <SelectElement
        name="experience"
        rules="required"
        :placeholder="jText._('MOD_REACTIVE_FORMS_LVM_VACANCY_EXPERIENCE_PLACEHOLDER')"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_EXPERIENCE')"
        :native="false"
        :floating="false"
        :items="employmentDurations"
        :columns="{ default: 12, md: 6 }"
    />
    <TextElement
        name="linkedin_address"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_LINKEDIN_ADDRESS')"
        :rules="[{
          required: ['cv_file', null]
        }]"
        :columns="showCertificateField ? { default: 12 } : { default: 12, md: 6 }"
    />
    <FileElement
        name="cv_file"
        :rules="[{required: ['linkedin_address', null]}, 'file', 'mimes:pdf,doc,docx', 'max:10240']"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_CV_FILE')"
        :drop="true"
        :upload-temp-endpoint="false"
        :columns="{ default: 12, md: 6 }"
        :add-classes="{
          FilePreview: {
            container: 'vf-input-group',
            wrapper: 'vf-input-field'
          }
        }"
    />
    <FileElement
        name="motivation_file"
        rules="nullable|file|mimes:pdf,doc,docx|max:10240"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_MOTIVATION_FILE')"
        :drop="true"
        :upload-temp-endpoint="false"
        :columns="{ default: 12, md: 6 }"
        :add-classes="{
          FilePreview: {
            container: 'vf-input-group',
            wrapper: 'vf-input-field'
          }
        }"
    />
    <TextareaElement
        name="comments"
        :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_COMMENT')"
        :autogrow="true"
    />
    <CheckboxElement
        name="apply_to_related_vacancies"
        :text="jText._('MOD_REACTIVE_FORMS_LVM_VACANCY_APPLY_TO_RELATED_VACANCIES')"
    />
    <CaptchaElement name="g_recaptcha_response"/>
    <HiddenElement name="id" :default="vacancyId"/>
    <ButtonElement
        name="submit"
        align="center"
        :button-label="jText._('MOD_REACTIVE_FORMS_LVM_VACANCY_BUTTON_LABEL')"
        :submits="true"
        :add-class="{button: ['btn']}"
    />
  </Vueform>
</template>
